import { AgeRestriction } from '@diritto/common/content/constants';
import { EpisodeOrderType } from '@diritto/common/episode/constants';
import { EpisodeReadRecordDto } from '@diritto/common/recentlyRead/dtos/episdoe-read-record.dto';
import { EpisodePublicRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { accurateItems, flatItems, toMapByKey } from '@diritto/common/utils/array';
import styled from '@emotion/styled';
import { ContentDetailEpisodeItem } from 'app-components/contents/detail/ContentDetailEpisodeItem';
import { useAuthState } from 'contexts/AuthProvider';
import { useGetEpisodeReadRecordsQuery } from 'queries/episodeReadRecord/useGetEpisodeReadRecordsQuery';
import { useGetRelayContentEpisodesInfiniteQuery } from 'queries/relay/useGetRelayContentEpisodesInfiniteQuery';
import React, { useMemo } from 'react';
import { EmptyList } from 'ui-components/common/EmptyList';
import { InfiniteScrollWrapper } from 'ui-components/common/InfiniteScrollWrapper';
import { TransitionLoading } from 'ui-components/common/Loading';
import { useClient } from 'ui-hooks/useClient';

interface ContentDetailEpisodeListProps {
  contentId: number;
  isAdultContent: boolean;
  selectedEpisodeId?: number;
  episodeOrderType: EpisodeOrderType;
  className?: string;
  replaceLink?: boolean;
  onClick?: () => void;
}

export const ContentDetailEpisodeList = ({
  contentId,
  isAdultContent,
  selectedEpisodeId,
  episodeOrderType,
  className,
  replaceLink = false,
  onClick,
}: ContentDetailEpisodeListProps) => {
  const client = useClient();
  const { user } = useAuthState();

  const { data, isLoading, hasNextPage, fetchNextPage } = useGetRelayContentEpisodesInfiniteQuery(
    contentId,
    episodeOrderType,
  );

  const { episodeIds, episodes } = useMemo(
    () => ({
      episodeIds: flatItems<number>('episodeIds', data?.pages) ?? [],
      episodes: accurateItems<EpisodePublicRelayDto>('episodes', data?.pages) ?? {},
    }),
    [data],
  );

  const { data: episodeReadRecordData } = useGetEpisodeReadRecordsQuery(contentId, !!user);
  const readEpisodes = useMemo(
    () =>
      episodeReadRecordData &&
      (toMapByKey('episodeId', episodeReadRecordData?.episodeReadRecords) as Record<string, EpisodeReadRecordDto>),
    [episodeReadRecordData],
  );

  return (
    <StyledEpisodeArea loadMore={fetchNextPage} hasNext={hasNextPage} className={className} showLoading={isLoading}>
      <TransitionLoading isShow={isLoading} />

      {!isLoading && episodeIds.length === 0 && <EmptyList description="등록된 회차가 없어요." />}
      <ul>
        {episodeIds.map(it => {
          const episode = episodes[it];
          if (client.isApp && !user?.isAdult && episode && episode.ageRestriction === AgeRestriction.Adult) {
            return null;
          }

          return (
            episode && (
              <li key={episode.id}>
                <ContentDetailEpisodeItem
                  isSelected={selectedEpisodeId === episode.id}
                  volume={episode.volume}
                  episode={episode}
                  readEpisode={readEpisodes && readEpisodes[episode.id]}
                  replyCount={episode.counts.replyCount}
                  recommendCount={episode.counts.recommendCount}
                  isAdultContent={isAdultContent}
                  replaceLink={replaceLink}
                  onClick={onClick}
                />
              </li>
            )
          );
        })}
      </ul>
    </StyledEpisodeArea>
  );
};

const StyledEpisodeArea = styled(InfiniteScrollWrapper)`
  position: relative;
  display: flex;
  flex-direction: column;
`;
